/**
 * The help text behind the question mark of a calibration value (owner, 2026-10-12): what it
 * does, how it is used, its default, and what a higher and a lower value do. One list, so a
 * default is written once and reads the same on every page that shows the value.
 */
export interface ValueHelp {
  does: string;
  used: string;
  default: string;
  higher: string;
  lower: string;
  /** The names of the last two lines, for a value that is not a number: a switch reads
   *  "On" / "Off", a choice names its options. A number reads "Higher" / "Lower". */
  labels?: readonly [string, string];
}

/** The five lines, in one order, for `InfoHint`. The tooltip keeps the line breaks. */
export function valueHelpText(h: ValueHelp): string {
  return [
    `What it does: ${h.does}`,
    `How it is used: ${h.used}`,
    `Default: ${h.default}`,
    `${h.labels?.[0] ?? "Higher"}: ${h.higher}`,
    `${h.labels?.[1] ?? "Lower"}: ${h.lower}`,
  ].join("\n");
}

/** Admin → AI matte: the calibration every customer inherits. */
export const SEGMENTATION_HELP = {
  model: {
    does: "Selects the AI model that removes the background of a product photo.",
    used: "The editor runs it for Remove background (AI), on the device.",
    default: "BiRefNet lite.",
    higher: "Sharper edges. It is a 489 MB download and it is slower.",
    lower: "Lighter and faster, with a rougher edge.",
    labels: ["BiRefNet full", "U²-Net-p"],
  },
  mask_gain: {
    does: "Multiplies how strongly the model keeps a pixel as product.",
    used: "Applied to the model's mask before the edge steps.",
    default: "1.00×.",
    higher: "Recovers faint or thin parts, such as a clear rim. Too high keeps background.",
    lower: "Removes weak areas. Too low cuts into the product.",
  },
  mask_gamma: {
    does: "Bends the mask curve: how soft areas are treated.",
    used: "Applied to the model's mask after the gain.",
    default: "1.00, which is no change.",
    higher: "Tightens the kept area.",
    lower: "Expands the kept area.",
  },
  mask_threshold: {
    does: "Makes every pixel below this confidence fully transparent.",
    used: "Removes a faint halo around the product. The scale is 0 to 255.",
    default: "0, which is off.",
    higher: "Removes more of the halo. Too high makes hard, jagged edges.",
    lower: "Keeps soft edges and more of the halo.",
  },
  edge_choke_px: {
    does: "Moves the cut edge inwards or outwards, in pixels.",
    used: "Applied to the edge of the mask after the model.",
    default: "+2 px.",
    higher: "Eats a fringe or halo at the edge.",
    lower: "A negative value grows an edge back that was cut too far.",
  },
  edge_feather_px: {
    does: "Softens the cut edge, in pixels.",
    used: "Blurs only the edge of the mask.",
    default: "1 px.",
    higher: "A softer edge. Too high looks blurred.",
    lower: "A crisper edge. 0 is a hard cut.",
  },
  despill_amount: {
    does: "Removes a colour cast on the kept edge pixels.",
    used: "Only by the classical fallback. The BiRefNet matte ignores it.",
    default: "1.00.",
    higher: "Removes more of the cast.",
    lower: "Keeps the original edge colour.",
  },
  preserve_luma: {
    does: "Keeps the brightness of a pixel when its colour cast is removed.",
    used: "Only by the classical fallback, together with Edge despill.",
    default: "On.",
    higher: "Edges stay as bright as they were.",
    lower: "The edge pixels can get darker.",
    labels: ["On", "Off"],
  },
  key_method: {
    does: "Chooses how the print guide is keyed. Colour to alpha removes only the magenta from each pixel and keeps what is left, so shadows and highlights stay as a see-through layer over the design, as the Transparent tab does. Purity clears each pixel as far as it is magenta. Spread grows from strong magenta and decides each pixel yes or no.",
    used: "When a template made from Generate is keyed. Try each in the editor under Marker & keying.",
    default: "Colour to alpha.",
    higher: "Colour to alpha keeps the most texture; Spread keeps the least.",
    lower: "Spread is the key used before v0.2.130.",
  },
  key_noise_floor: {
    does: "Magenta below this amount counts as no magenta at all.",
    used: "By Colour to alpha, as on the Transparent tab.",
    default: "4, as on the Transparent tab.",
    higher: "Faint pink tints stay as they are.",
    lower: "Even a faint pink tint starts to clear.",
  },
  key_flat_sample: {
    does: "Which pixels set the reference magenta: those above this share of the strongest magenta.",
    used: "By Colour to alpha, as on the Transparent tab.",
    default: "60%.",
    higher: "Only the purest magenta sets the reference.",
    lower: "Shaded magenta joins the reference, so shadows clear more.",
  },
  key_snap_low: {
    does: "A pixel this close to fully clear is made fully clear.",
    used: "By Colour to alpha, as on the Transparent tab.",
    default: "4%.",
    higher: "More faint haze over the design is removed.",
    lower: "More of the faint layer stays.",
  },
  key_snap_high: {
    does: "A pixel this close to fully solid is made fully solid.",
    used: "By Colour to alpha, as on the Transparent tab.",
    default: "96%.",
    higher: "Fewer pixels snap to solid; edges keep more see-through.",
    lower: "More nearly solid pixels become solid.",
  },
  key_purity_start: {
    does: "The purity where keying begins. Purity is how magenta a pixel is, whatever its brightness: #FF00FF and dark magenta are 100%; grey, black, white and green are 0%.",
    used: "By the purity key only.",
    default: "15%.",
    higher: "Pink and faint magenta stay more opaque.",
    lower: "Fainter magenta starts to clear.",
  },
  key_purity_full: {
    does: "The purity where a pixel is fully transparent. Between the start and this value it clears on a smooth ramp.",
    used: "By the purity key only.",
    default: "60%.",
    higher: "Only very pure magenta clears fully; a softer, longer ramp.",
    lower: "Clears fully sooner; a harder edge.",
  },
  key_dark_keep: {
    does: "Keeps darker magenta more opaque, so deep shadows in the guide keep some body over the design.",
    used: "By the purity key only.",
    default: "0%: dark and bright magenta clear alike.",
    higher: "Deep shadows stay darker and more solid.",
    lower: "Shadows clear like the rest; their light still shades the design.",
  },
  key_tolerance: {
    does: "Decides how weak a magenta still counts as print guide when the key removes it.",
    used: "When a template made from Generate is keyed. Try it in the editor under Marker & keying, then save it here.",
    default: "50%.",
    higher: "More of the faint, shaded magenta is removed, near the camera lip and the edges too.",
    lower: "Only strong magenta is removed; faint magenta stays as part of the case.",
  },
  key_softness: {
    does: "Sets the soft step where the key meets what it keeps: the camera lip, the openings and the rim.",
    used: "When a template made from Generate is keyed. Try it in the editor under Marker & keying, then save it here.",
    default: "1 px.",
    higher: "Smoother edges round what is kept. 4 px can look slightly blurred.",
    lower: "Harder edges. 0 px leaves the pixel steps of the image.",
  },
  key_colour_removal: {
    does: "Takes the magenta cast out of what the key keeps beside the print guide, such as the camera lip and the rim. Its light stays, as grey.",
    used: "When a template made from Generate is keyed. Try it in the editor under Marker & keying, then save it here.",
    default: "100%: what the key keeps beside the guide is black and white.",
    higher: "Less pink on the kept edges. 100% leaves them grey.",
    lower: "More of the original colour on the kept edges.",
  },
  key_shadow_depth: {
    does: "Sets how dark the case's own shadows sit over the artwork, for example around the camera bump.",
    used: "When the print area is keyed out and the artwork is placed.",
    default: "150%. 100% is the shadow exactly as the image recorded it.",
    higher: "Deeper shadows and more definition. Very high values stop at the engine's limit.",
    lower: "Lighter shadows and a flatter print.",
  },
  key_highlight_depth: {
    does: "Sets how bright the case's own highlights sit over the artwork, for example the lit rim of the camera bump.",
    used: "When the print area is keyed out and the artwork is placed.",
    default: "140%. 100% is the highlight exactly as the image recorded it.",
    higher: "Brighter highlights and more gloss. Very high values stop at the engine's limit.",
    lower: "Softer highlights and a more matt print.",
  },
  print_shading: {
    does: "Lays the case's own shadows and highlights over the design, so it looks printed under the shell.",
    used: "Each time a mock-up is rendered. It is the master strength of the print-area light.",
    default: "60%.",
    higher: "More of the case's light on the design. Above 100% exaggerates it.",
    lower: "A flatter design. 0% is a flat sticker.",
  },
  print_highlights: {
    does: "Adds the case's own highlights on top of the design.",
    used: "Works while Artwork shading is above 0%. It is what makes a black or dark design show depth.",
    default: "40%.",
    higher: "Dark designs catch more light. 100% can look lacquered.",
    lower: "Dark designs look flatter. 0% adds no highlight.",
  },
  print_black: {
    does: "Sets how dark the darkest part of a design prints. A real print has no pure black.",
    used: "On every render; a template opens at it, and an admin can change it per template.",
    default: "0%, which is pure black.",
    higher: "Black prints as a lighter grey.",
    lower: "Black prints deeper.",
  },
  print_white: {
    does: "Sets how bright the brightest part of a design prints. A real print has no pure white.",
    used: "On every render; a template opens at it, and an admin can change it per template.",
    default: "100%, which is pure white.",
    higher: "White prints brighter.",
    lower: "White prints as a light grey.",
  },
  print_gradient: {
    does: "Adds a studio light from the top of the print area to its bottom.",
    used: "For a flat panel that recorded no light of its own, such as a generated print guide.",
    default: "42%.",
    higher: "A brighter top and a darker bottom.",
    lower: "A more even print. 0% adds no gradient.",
  },
  print_grain: {
    does: "Adds a fine matt grain to the design.",
    used: "On every render. It reads strongest on dark colours.",
    default: "55%.",
    higher: "A rougher, more matt surface.",
    lower: "A cleaner surface. 0% adds no grain.",
  },
  print_sheen: {
    does: "Adds a soft gloss over the design.",
    used: "On every render. It reads strongest on dark colours and near the top.",
    default: "120%.",
    higher: "A glossier print.",
    lower: "A more matt print. 0% adds no sheen.",
  },
  shading_smoothing: {
    does: "Blurs the fine detail of the shading before it is laid over the design.",
    used: "On every render of every template. It removes the blocks and steps that an AI image carries.",
    default: "50%.",
    higher: "A smoother print with no pixelation. 100% also softens thin rims.",
    lower: "A sharper print. 0% copies every pixel of the image, blocks included.",
  },
  case_highlights: {
    does: "Adds the clear case's own highlights over the design: the rims, the camera lip and thin reflections.",
    used: "On every render of a clear-case template. The highlights are read from the clear image, because the print guide is flat.",
    default: "0%, which is off.",
    higher: "Brighter rims and reflections. A thin edge of the phone behind the case can also catch light.",
    lower: "Fainter highlights. 0% adds none.",
  },
  flatten_magenta: {
    does: "Paints the back of a new Generate print guide flat pure magenta, so only the camera lip keeps the AI's shading.",
    used: "Each time a Generate job makes its magenta image. Images made before keep how they were made.",
    default: "Off: the print guide keeps the AI's own magenta, with all its shading and detail.",
    higher: "On: flat magenta. Use it when the AI lets the phone's logo or camera show through the magenta.",
    lower: "Off: the AI's shading, highlights and texture stay in the print.",
    labels: ["On", "Off"],
  },
  key_depth_all_templates: {
    does: "Puts the light values above into every template: shading, highlights, the two depths, gradient, grain and sheen.",
    used: "Each time you save with it on. A template takes the values until someone changes it in the editor and saves it.",
    default: "Off: the values are defaults for new templates only.",
    higher: "Save with it on: every saved template takes these values now. Each template can still be changed after.",
    lower: "Save with it off: no template changes. Templates keep the values they have.",
    labels: ["On", "Off"],
  },
} as const satisfies Record<string, ValueHelp>;

/** Template editor → Remove background (AI): the light of the print area. */
export const PRINT_LIGHT_HELP = {
  shading: {
    does: "Lays the case's own shadows and highlights over the design, so it looks printed under the shell.",
    used: "Each time the mock-up is rendered. It is the master strength of the print-area light.",
    default: "60%.",
    higher: "More of the case's light on the design. Above 100% exaggerates it.",
    lower: "A flatter design. 0% is a flat sticker.",
  },
  highlights: {
    does: "Adds the case's own highlights on top of the design.",
    used: "Works while Artwork shading is on. It is what makes a black or dark design show depth.",
    default: "40% on a new template.",
    higher: "Dark designs catch more light. 100% can look lacquered.",
    lower: "Dark designs look flatter. 0% adds no highlight.",
  },
  inkBlack: {
    does: "Sets how dark the darkest part of the design prints. A real print has no pure black.",
    used: "Each time the mock-up is rendered, before the case's light reaches the design.",
    default: "0%, or the value set in Admin → AI matte.",
    higher: "Black prints as a lighter, softer grey.",
    lower: "Black prints deeper. 0% is pure black.",
  },
  inkWhite: {
    does: "Sets how bright the brightest part of the design prints. A real print has no pure white.",
    used: "Each time the mock-up is rendered, before the case's light reaches the design.",
    default: "100%, or the value set in Admin → AI matte.",
    higher: "White prints brighter. 100% is pure white.",
    lower: "White prints as a light grey.",
  },
  shadowDepth: {
    does: "Sets how dark the case's own shadows sit over the design, for example around the camera bump.",
    used: "For this template only. The value for every customer is in Admin → AI matte.",
    default: "150%, or the value set in Admin → AI matte.",
    higher: "Deeper shadows and more definition.",
    lower: "Lighter shadows. 100% is the shadow as the image recorded it.",
  },
  highlightDepth: {
    does: "Sets how bright the case's own highlights sit over the design, for example the lit rim of the camera bump.",
    used: "For this template only. The value for every customer is in Admin → AI matte.",
    default: "140%, or the value set in Admin → AI matte.",
    higher: "Brighter highlights and more gloss.",
    lower: "Softer highlights. 100% is the highlight as the image recorded it.",
  },
  gradient: {
    does: "Adds a studio top-light: brighter at the top of the print area, darker at the bottom.",
    used: "For a flat panel that recorded no light of its own, such as a generated print guide.",
    default: "42%.",
    higher: "A stronger top-to-bottom fade.",
    lower: "A more even panel. 0% is off.",
  },
  grain: {
    does: "Adds a fine matt print texture, strongest in dark colours.",
    used: "Rendered the same on every device.",
    default: "55%.",
    higher: "A more visible texture.",
    lower: "A smoother print. 0% is off.",
  },
  sheen: {
    does: "Adds reflected glare: a band along the edges and soft streaks.",
    used: "Strongest toward the top and on dark colours.",
    default: "120%.",
    higher: "A glossier print.",
    lower: "A more matt print. 0% is off.",
  },
} as const satisfies Record<string, ValueHelp>;

/** Admin → AI providers. */
export const PROVIDERS_HELP = {
  provider: {
    does: "Selects the AI service that makes every generated image.",
    used: "Every generation that runs through the cloud uses it.",
    default: "Higgsfield.",
    higher: "Jobs that start after the change run on the new provider. It needs its own API key.",
    lower: "Running jobs finish on the provider they started on.",
    labels: ["Change it", "Keep it"],
  },
  keyStatus: {
    does: "Shows which providers have an API key stored on the server.",
    used: "A provider with no key cannot run a job on the API wallet.",
    default: "No key until you add one.",
    higher: "The provider is ready to use.",
    lower: "Add a key in API keys before you select that provider.",
    labels: ["Green", "Grey"],
  },
  apiKey: {
    does: "Stores the secret key the cloud uses to call a provider.",
    used: "It is kept on the server and is never shown again or sent to this device.",
    default: "Empty.",
    higher: "Replaces the stored key at once.",
    lower: "Removes the key. Jobs on that provider's API wallet then cannot start.",
    labels: ["Save", "Clear"],
  },
  validation: {
    does: "Asks the active provider if the selected models answer.",
    used: "Press Validate after you change a key or a model.",
    default: "Not checked until you press Validate.",
    higher: "The provider answered with the selected models.",
    lower: "Check the key and the model IDs.",
    labels: ["Reachable", "Unreachable"],
  },
  visionModel: {
    does: "Names the model each provider uses to detect the product in a photo.",
    used: "By the detect step. It makes no image.",
    default: "Blank, which is the provider's own default model.",
    higher: "The provider runs exactly that model.",
    lower: "The provider chooses its default model.",
    labels: ["A model ID", "Blank"],
  },
  imageModel: {
    does: "Names the model each provider uses to make images.",
    used: "By every generation on that provider's API wallet. A server setting can override it.",
    default: "Blank, which is the provider's own default model.",
    higher: "The provider runs exactly that model.",
    lower: "The provider chooses its default model.",
    labels: ["A model ID", "Blank"],
  },
  imageSizeQuality: {
    does: "Sets the size and the quality of every generated image.",
    used: "Recorded on each job when it is queued, so both steps of a job match.",
    default: "1k (square) and High.",
    higher: "A sharper image. It costs more credits and takes longer.",
    lower: "A cheaper and faster image with less detail.",
  },
  wallet: {
    does: "Selects which Higgsfield wallet pays for the designers' jobs.",
    used: "Fixed on each job when it is queued.",
    default: "Automatic.",
    higher: "Company subscription or API key: every job uses that one wallet.",
    lower: "Automatic: the subscription while it is connected, the API key while it is not.",
    labels: ["Fixed", "Automatic"],
  },
  subscriptionCallsPerDay: {
    does: "Limits how many subscription generations may start in one day.",
    used: "Counted for the whole company. A job over the limit waits.",
    default: "Blank, which uses Calls per day in Limits & approvals.",
    higher: "More jobs per day, and more credits spent per day.",
    lower: "Fewer jobs per day. Jobs over the limit wait for the next day.",
  },
  subscriptionInFlight: {
    does: "Limits how many subscription steps may run at the same time.",
    used: "A step counts from its submit until its image is collected.",
    default: "4 steps.",
    higher: "More jobs in parallel. Higgsfield can slow down or refuse.",
    lower: "Jobs run more one by one, and a queue takes longer.",
  },
  maxCreditsPerStage: {
    does: "Refuses a generation step whose quoted price is above this number of credits.",
    used: "Checked against Higgsfield's own quote, before anything is spent.",
    default: "4 credits.",
    higher: "Allows 2k and 4k images and expensive models.",
    lower: "Blocks expensive settings. Too low blocks every job.",
  },
  console: {
    does: "Opens Higgsfield's own site for credits, invoices and the plan.",
    used: "Those are managed at Higgsfield, not in this app.",
    default: "A link. It holds no setting.",
    higher: "Opens in your browser.",
    lower: "Nothing changes in this app.",
    labels: ["Open it", "Leave it"],
  },
  usdPerCredit: {
    does: "The money value of one Higgsfield credit, for the Usage page.",
    used: "Only to show costs in dollars. It changes no price and no limit.",
    default: "$0.04. It is set on the server.",
    higher: "The Usage page shows higher costs.",
    lower: "The Usage page shows lower costs.",
  },
  customersCanGenerate: {
    does: "Lets customers start generations, which spend credits.",
    used: "Checked each time a customer opens Generate or starts a job. Admins can always generate.",
    default: "Off.",
    higher: "Customers can generate, and they spend the company's credits.",
    lower: "Customers place artwork in Mock-ups and never spend credits.",
    labels: ["On", "Off"],
  },
  productHeight: {
    does: "Sets how tall the product is drawn in a case-job image, as a share of the image height.",
    used: "A designer's case job always uses it. An admin can choose per job.",
    default: "95%.",
    higher: "A larger product with less empty margin.",
    lower: "A smaller product with more margin.",
  },
  stageModels: {
    does: "Selects the model of each generation step, for each wallet.",
    used: "Step 1 makes the product image. Step 2 adds the print guide. Each step is prompted in the form of its own model.",
    default: "The model first in each list.",
    higher: "A stronger model gives better detail and costs more credits.",
    lower: "A faster model costs less and can miss detail.",
    labels: ["Stronger model", "Faster model"],
  },
  callsPerDay: {
    does: "Limits how many AI calls the whole platform may make in one day.",
    used: "Counted across every customer on the API wallet. The subscription uses it when its own limit is blank.",
    default: "Blank, which is no limit.",
    higher: "More calls per day and a higher possible cost.",
    lower: "A lower cost ceiling. Calls over the limit are refused until the next day.",
  },
  requireApproval: {
    does: "Makes a finished template wait for an admin's review before others can use it.",
    used: "Applies to every template that is published.",
    default: "Off.",
    higher: "Templates wait in Admin → Approvals until an admin approves them.",
    lower: "A template is usable as soon as it is published.",
    labels: ["On", "Off"],
  },
  logRetention: {
    does: "Sets how many days the app's own event logs are kept.",
    used: "Older events are deleted by the cloud.",
    default: "30 days. The minimum is 1.",
    higher: "A longer history for finding a fault, and more stored data.",
    lower: "Less stored data, and a shorter history.",
  },
} as const satisfies Record<string, ValueHelp>;
